import React, { useEffect, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import "./CVM.scss";
import { Breadcrumb } from "components";
import { t } from "react-switch-lang";
import api from "components/api/api";

am4core.useTheme(am4themes_animated);

/* ===================== DATA ===================== */

const tableData = [
  {
    omanMall: "All Services",
    transactions: 174,
    served: 174,
    notServed: 0,
    avgServiceTime: "00:06:00",
    avgWaitingTime: "00:08:30",
  },
  {
    omanMall: "BAQATI",
    transactions: 108,
    served: 107,
    notServed: 1,
    avgServiceTime: "00:07:54",
    avgWaitingTime: "00:05:48",
  },
  {
    omanMall: "Business",
    transactions: 1,
    served: 1,
    notServed: 0,
    avgServiceTime: "00:01:36",
    avgWaitingTime: "00:00:00",
  },
  {
    omanMall: "Complaint",
    transactions: 13,
    served: 13,
    notServed: 0,
    avgServiceTime: "00:07:00",
    avgWaitingTime: "00:07:12",
  },
  {
    omanMall: "HAYYAK",
    transactions: 271,
    served: 255,
    notServed: 16,
    avgServiceTime: "00:03:36",
    avgWaitingTime: "00:07:06",
  },
  {
    omanMall: "HBB",
    transactions: 39,
    served: 39,
    notServed: 0,
    avgServiceTime: "00:05:24",
    avgWaitingTime: "00:08:30",
  },
  {
    omanMall: "Inquiry",
    transactions: 707,
    served: 705,
    notServed: 2,
    avgServiceTime: "00:04:12",
    avgWaitingTime: "00:07:06",
  },
  {
    omanMall: "Total for Service Group",
    transactions: 1313,
    served: 1294,
    notServed: 19,
    avgServiceTime: "00:04:42",
    avgWaitingTime: "00:07:12",
    isTotal: true,
  },
];

const chartData = tableData.filter(
  (item) => item.omanMall !== "Total for Service Group",
);

const timeToMinutes = (time) => {
  const [h, m, s] = time.split(":").map(Number);
  return h * 60 + m + s / 60;
};

/* ===================== DONUT CREATOR ===================== */

const createDonut = (ref, data, tooltip) => {
  const chart = am4core.create(ref.current, am4charts.PieChart);
  chart.radius = am4core.percent(85);
  chart.innerRadius = am4core.percent(65);

  const series = chart.series.push(new am4charts.PieSeries());
  series.dataFields.value = "value";
  series.dataFields.category = "category";

  // 🔥 NO LABEL CLUTTER
  series.labels.template.disabled = true;
  series.ticks.template.disabled = true;

  // ✅ HOVER FIX
  series.slices.template.states.getKey("hover").properties.scale = 1.03;
  series.slices.template.states.getKey("hover").properties.shiftRadius = 0;

  series.slices.template.tooltipText = tooltip;

  // 🎨 COLORS
  series.colors.list = [
    am4core.color("#6C8CF5"),
    am4core.color("#5B6EE1"),
    am4core.color("#7B6DE5"),
    am4core.color("#8F6FE8"),
    am4core.color("#A06FEA"),
    am4core.color("#B66FEA"),
    am4core.color("#D46FEA"),
  ];

  // 📌 LEGEND BELOW (WRAPS)
  chart.legend = new am4charts.Legend();
  chart.legend.position = "bottom";
  chart.legend.align = "center";
  chart.legend.width = am4core.percent(100);
  chart.legend.maxWidth = 350;

  chart.legend.itemContainers.template.layout = "horizontal";
  chart.legend.labels.template.wrap = true;
  chart.legend.labels.template.maxWidth = 110;
  chart.legend.labels.template.fontSize = 12;

  chart.legend.markers.template.width = 12;
  chart.legend.markers.template.height = 12;

  chart.data = data;
  return chart;
};

/* ===================== COMPONENT ===================== */

function CVM(props) {
  const servedRef = useRef(null);
  const serviceRef = useRef(null);
  const waitingRef = useRef(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [regionData, setRegionData] = useState({});
  const [regionNames, setRegionNames] = useState([]);

  const [selectedRegion, setSelectedRegion] = useState("All Regions");
  const [selectedOutlet, setSelectedOutlet] = useState("All Outlet");
  const [outlets, setOutlets] = useState([]);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await api.fetchUsers(); // same API you already use
        const users = Array.isArray(res.data?.data) ? res.data.data : [];

        const regionMap = { "All Regions": {} };

        users.forEach((u) => {
          if (!u.region || !u.outlet) return;

          if (!regionMap[u.region]) regionMap[u.region] = {};
          if (!regionMap[u.region][u.outlet])
            regionMap[u.region][u.outlet] = [];

          regionMap[u.region][u.outlet].push(u.id);
        });

        setRegionData(regionMap);
        setRegionNames(Object.keys(regionMap));
      } catch (e) {
        console.error("Failed to load regions", e);
      }
    };

    fetchRegions();
  }, []);

  const handleRegionChange = (region) => {
    setSelectedRegion(region);

    if (region === "All Regions") {
      const allOutlets = Object.values(regionData).flatMap((r) =>
        Object.keys(r || {}),
      );

      setOutlets(allOutlets);
      setSelectedOutlet("All Outlet");
    } else {
      const regionOutlets = Object.keys(regionData[region] || {});
      setOutlets(regionOutlets);
      setSelectedOutlet("All Outlet");
    }
  };

  const handleOutletChange = (outlet) => {
    setSelectedOutlet(outlet);
  };

  useEffect(() => {
    const servedChart = createDonut(
      servedRef,
      chartData.map((i) => ({
        category: i.omanMall,
        value: i.served,
      })),
      "{category}\nServed: {value}\n({value.percent.formatNumber('#.0')}%)",
    );

    const serviceChart = createDonut(
      serviceRef,
      chartData.map((i) => ({
        category: i.omanMall,
        value: timeToMinutes(i.avgServiceTime),
      })),
      "{category}\nAvg Service: {value.formatNumber('#.0')} mins",
    );

    const waitingChart = createDonut(
      waitingRef,
      chartData.map((i) => ({
        category: i.omanMall,
        value: timeToMinutes(i.avgWaitingTime),
      })),
      "{category}\nAvg Waiting: {value.formatNumber('#.0')} mins",
    );

    return () => {
      servedChart.dispose();
      serviceChart.dispose();
      waitingChart.dispose();
    };
  }, []);
  const renderFilters = () => (
    <>
      {/* DATE RANGE */}
      <div className="col-md-3">
        <label>Start Date</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="form-control"
        />
      </div>

      <div className="col-md-3">
        <label>End Date</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="form-control"
          min={startDate}
        />
      </div>

      {/* REGION */}
      <div className="col-md-3">
        <label>Region</label>
        <select
          className="form-control"
          value={selectedRegion}
          onChange={(e) => handleRegionChange(e.target.value)}
        >
          {regionNames.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      {/* OUTLET */}
      <div className="col-md-3">
        <label>Outlet</label>
        <select
          className="form-control"
          value={selectedOutlet}
          onChange={(e) => handleOutletChange(e.target.value)}
          disabled={selectedRegion === "All Regions"}
        >
          <option value="All Outlet">All Outlet</option>
          {outlets.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <div className="cvm-dashboard">
      <Breadcrumb title={t("CVM")} />
      {/* ================= FILTERS ================= */}

      <div className="row filter-section">{renderFilters()}</div>

      {/* ================= TABLE ================= */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="header-gray">Oman Mall</th>
              <th className="header-red">Number Of Transactions</th>
              <th className="header-red">Served</th>
              <th className="header-red">Not Served</th>
              <th className="header-red">Average Service Time (H:M:S)</th>
              <th className="header-red">Average Waiting Time (H:M:S)</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, index) => (
              <tr key={index} className={row.isTotal ? "total-row" : ""}>
                <td className="cell-gray">{row.omanMall}</td>
                <td>{row.transactions}</td>
                <td>{row.served}</td>
                <td>{row.notServed}</td>
                <td>{row.avgServiceTime}</td>
                <td>{row.avgWaitingTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ================= CHARTS ================= */}
      <div className="charts-container">
        <div className="chart-wrapper">
          <h3 className="chart-title">Served</h3>
          <div ref={servedRef} className="chart" />
        </div>

        <div className="chart-wrapper">
          <h3 className="chart-title">Average Service Time</h3>
          <div ref={serviceRef} className="chart" />
        </div>

        <div className="chart-wrapper">
          <h3 className="chart-title">Average Waiting Time</h3>
          <div ref={waitingRef} className="chart" />
        </div>
      </div>
    </div>
  );
}

export default CVM;
